import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { Box, CircularProgress, Typography } from '@mui/material';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  DatePicker,
  FieldWrapperVertical,
  SnackbarApiErrorMessage,
  TimePicker,
  useDataProvider,
  useDialog,
  useLocalization,
  useMasterData,
  useSnackbar,
} from '@ui-framework/index';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import dayjs from 'dayjs';
import { nanoid } from 'nanoid';
import IconLibrary from '../IconLibrary';
import DialogCloseButton from './DialogCloseButton';

function UpdateAppointmentDate({ open, onClose, jobInfo }) {
  const { formatMessage } = useLocalization();
  const snackbar = useSnackbar();
  const {
    id: jobId,
    appointmentDate,
    appointmentArrivalStartTime,
    appointmentArrivalEndTime,
  } = jobInfo;

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { getApiUrl, put } = useDataProvider(provider);

  const schema = yup.object().shape({
    appointmentDate: yup
      .string()
      .required(
        formatMessage({
          id: 'APPOINTMENT_DATE_REQUIRED',
          defaultMessage: 'Date is required',
        }),
      )
      .test(
        'is-date-in-past',
        formatMessage({
          id: 'APPOINTMENT_DATE_PAST',
          defaultMessage: 'Date cannot be in the past',
        }),
        value => {
          return !dayjs(value).isBefore(dayjs(), 'day');
        },
      ),
  });

  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('');
  const [changedInput, setChnagedInput] = useState('');
  const [dialogError, setDialogError] = useState('');
  const { confirmationRef } = useDialog();
  const { reschedulableJobStatuses: validRescheduleStatuses } = useMasterData();

  const methods = useForm({ resolver: yupResolver(schema) });
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = methods;

  const closeDialog = () => {
    onClose(null);
  };

  const buttons = [
    {
      children: formatMessage({
        id: 'RESCHEDULE',
        defaultMessage: 'Reschedule',
      }),
      type: 'Submit',
      variant: 'contained',
      color: 'primary',
      show: ['MAIN'],
      startIcon: <IconLibrary.Reschedule />,
    },
    {
      children: formatMessage({
        id: 'CONFIRM',
        defaultMessage: 'Confirm',
      }),
      type: 'Submit',
      variant: 'contained',
      color: 'primary',
      show: ['CONFIRM'],
      startIcon: <IconLibrary.Yes />,
    },
    {
      children: formatMessage({ id: 'CANCEL', defaultMessage: 'Cancel' }),
      onClick: () => {
        closeDialog();
      },
      variant: 'contained',
      color: view === 'DISABLED' ? 'primary' : 'secondary',
      startIcon: <IconLibrary.Cancel />,
    },
  ];

  useEffect(() => {
    if (open) {
      methods.reset();
      if (!validRescheduleStatuses.includes(jobInfo?.serviceJobStatus)) {
        setView('DISABLED');
      } else {
        setView('MAIN');
      }
    }
  }, [open, jobId]);

  function rescheduleButtonClick(data) {
    const normalize = v => (v === undefined ? null : v);
    const isDirty =
      normalize(data?.appointmentDate) !== appointmentDate ||
      normalize(data?.appointmentArrivalStartTime) !==
        appointmentArrivalStartTime ||
      normalize(data?.appointmentArrivalEndTime) !== appointmentArrivalEndTime;

    if (isDirty) {
      setDialogError('');
      setChnagedInput(data);
      setView('CONFIRM');
    } else {
      setDialogError('Please modify at least one field to proceed.');
    }
  }

  function getFormattedDate(data) {
    let message = `${data?.appointmentDate}`;
    if (data?.appointmentArrivalStartTime) {
      message += `, After ${dayjs(
        data?.appointmentArrivalStartTime,
        'HH:mm',
      )?.format('h:mm A')}`;
    }
    if (data?.appointmentArrivalEndTime) {
      message += `, Before ${dayjs(
        data?.appointmentArrivalEndTime,
        'HH:mm',
      )?.format('h:mm A')}`;
    }
    return message;
  }

  async function onFormSubmit(data) {
    setLoading(true);

    if (view !== 'CONFIRM') {
      rescheduleButtonClick(data);
    } else {
      const body = {
        appointmentDate: data?.appointmentDate,
        appointmentArrivalStartTime: data?.appointmentArrivalStartTime || null,
        appointmentArrivalEndTime: data?.appointmentArrivalEndTime || null,
        recordVersion: jobInfo?.recordVersion,
      };
      const response = await put(
        `${getApiUrl()}/sxcl/v1/service-jobs/${jobId}`,
        body,
      );
      if (response.ok) {
        confirmationRef.current.init({
          message: 'Job was rescheduled successfully',
          positiveButtonText: 'Close',
          width: 360,
          title: 'Job Reschedule',
          onChange: () => {
            confirmationRef.current.setOpen(false);
            onClose(nanoid());
          },
        });
        confirmationRef.current.setOpen(true);
      } else {
        snackbar.showMessage({
          type: 'error',
          data: <SnackbarApiErrorMessage errors={response?.errors} />,
        });
      }
    }
    setLoading(false);
  }

  return (
    <Dialog open={open} onClose={() => closeDialog()} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconLibrary.RescheduleServiceJob />
        {formatMessage({
          id: 'RESCHEDULE_JOB',
          defaultMessage: 'Reschedule Job',
        })}
        &nbsp;&ndash;
        <Typography color="primary" variant="body">
          {jobInfo?.serviceJobNumber}
        </Typography>
        <DialogCloseButton disabled={loading} onClose={() => closeDialog()} />
      </DialogTitle>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <DialogContent>
            {dialogError && (
              <Typography color="error" textAlign="center" sx={{ mb: 2 }}>
                {dialogError}
              </Typography>
            )}
            {loading ? (
              <Box textAlign="center">
                <CircularProgress size="30px" />
                <Typography>Processing</Typography>
              </Box>
            ) : (
              <>
                {view === 'MAIN' && (
                  <Grid2 container spacing={1}>
                    <Grid2 xs={4}>
                      <FieldWrapperVertical
                        field={{
                          name: 'appointmentDate',
                          label: {
                            localeId: 'appointmentDate',
                            defaultMessage: 'Appointment Date',
                          },
                        }}
                        errors={errors}>
                        <DatePicker disablePast name="appointmentDate" />
                      </FieldWrapperVertical>
                    </Grid2>
                    <Grid2 xs={3}>
                      <Controller
                        name="appointmentArrivalStartTime"
                        control={control}
                        render={({ field }) => (
                          <FieldWrapperVertical
                            field={{
                              name: 'appointmentArrivalStartTime',
                              label: {
                                localeId: 'SERVICE_DATE_AFTER_HOUR_LABEL',
                                defaultMessage: 'After',
                              },
                            }}>
                            <TimePicker {...field} />
                          </FieldWrapperVertical>
                        )}
                      />
                    </Grid2>
                    <Grid2 xs={3}>
                      <Controller
                        name="appointmentArrivalEndTime"
                        control={control}
                        render={({ field }) => (
                          <FieldWrapperVertical
                            field={{
                              name: 'appointmentArrivalEndTime',
                              label: {
                                localeId: 'SERVICE_DATE_BEFORE_HOUR_LABEL',
                                defaultMessage: 'Before',
                              },
                            }}>
                            <TimePicker {...field} />
                          </FieldWrapperVertical>
                        )}
                      />
                    </Grid2>
                  </Grid2>
                )}
                {view === 'CONFIRM' && (
                  <Typography textAlign="center">
                    Appointment Date has been changed. Please confirm your
                    intention to perform the service on{' '}
                    {getFormattedDate(changedInput)}. Ensure that the customer
                    is informed of the updated appointment time.
                  </Typography>
                )}
                {view === 'DISABLED' && (
                  <Typography textAlign="center">
                    The Job&apos;s current status prevents rescheduling.
                  </Typography>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions
            display="flex"
            sx={{ justifyContent: 'center', my: 2 }}>
            {buttons
              .filter(btn => !btn.show || btn.show.includes(view))
              .map(btn => {
                const { children, ...rest } = btn;
                return (
                  <Button key={children} disabled={loading} {...rest}>
                    {children}
                  </Button>
                );
              })}
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
}

export default UpdateAppointmentDate;
